import { Injectable, inject } from '@angular/core';
import {
  AssetService,
  GridColumnV2,
  GridStateV2,
  GridStateChangeV2,
  GridUtilsV2,
  MultiselectV2ObservableItemSource,
  StoreV2,
} from '@schenck/rotec-components';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ToolingSearchResult {
  id: string;
  name: string;
  inventoryNumber: string;
  machines: string;
  toolingForRotor: string;
}

export interface ToolingSearchState extends GridStateV2<ToolingSearchResult> {
  selectedIds: string[];
}

@Injectable()
export class ToolingSearchService extends StoreV2<ToolingSearchState> {
  private readonly assetService = inject(AssetService);

  private _allResults$ = new BehaviorSubject<ToolingSearchResult[]>([]);

  allResults$: Observable<ToolingSearchResult[]> = this._allResults$.asObservable();
  selectedIds$: Observable<string[]> = this.state$.pipe(map((state) => state.selectedIds));
  firstSelectedResult$: Observable<ToolingSearchResult | null> = this.state$.pipe(
    map((state) => {
      if (state.selectedIds.length < 1) {
        return null;
      }
      return state.results.find((res) => res.id === state.selectedIds[0]);
    })
  );

  /**
   * Grid column definitions
   */
  columns: GridColumnV2[] = [
    {
      key: 'name',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Tooling',
      width: '25%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('name') },
    },
    {
      key: 'inventoryNumber',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Inventory Number',
      width: '20%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('inventoryNumber') },
    },
    {
      key: 'machines',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Machine(s)',
      width: '24%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('machines') },
    },
    {
      key: 'toolingForRotor',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'TOOLING_For Rotor(s)',
      width: '20%',
      filter: { type: 'text' },
    },
  ];

  constructor() {
    super(
      {
        filter: [],
        fulltext: '',
        page: 0,
        results: [],
        sort: [],
        total: 0,
        selectedIds: [],
        loading: true,
        type: '',
      },
      'ToolingSearch'
    );
    this.setAllResults();
  }

  private setAllResults() {
    combineLatest([this.assetService.toolings$, this.assetService.machines$])
      .pipe(
        map(([toolings, machines]) => {
          return toolings
            .filter((tooling) => !tooling.isDeactivated)
            .map((tooling) => {
              const machineToolings = tooling.machineIds.map((toolingId) => {
                const machineInfo = machines.find((machine) => toolingId === machine.id);
                if (typeof machineInfo !== 'undefined') {
                  return machineInfo.name + ' (' + machineInfo.id + ')';
                } else {
                  return 'Unknown Machine (' + toolingId + ')';
                }
              });
              return <ToolingSearchResult>{
                id: tooling.id,
                name: tooling.name,
                inventoryNumber: tooling.inventoryNo || '',
                machines: machineToolings.join(', ') || '',
                toolingForRotor: tooling.toolingForRotor || '',
              };
            });
        })
      )
      .subscribe((results) => {
        this._allResults$.next(results);
        this.search({});
      });
  }

  private getMultiselectFilterSource(key: keyof ToolingSearchResult) {
    return new MultiselectV2ObservableItemSource(this._allResults$, key);
  }

  /**
   * Invokes a search.
   * All parameters that are not present will be reused.
   */
  search(params: Partial<GridStateChangeV2>) {
    const currSearchParams: GridStateChangeV2 = {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      sort: this.state.sort,
      type: this.state.type,
    };
    const newSearchParams: GridStateChangeV2 = {
      ...currSearchParams,
      ...params,
    };

    this.patchState('update search params', newSearchParams);
    this.patchState('update results', { ...this.getResults(), loading: false });
  }

  /**
   * Invokes a fulltext search.
   */
  fulltextSearch(fulltext: string) {
    this.search({ fulltext });
  }

  /**
   * Set selected ids.
   */
  setSelected(selectedIds: string[]) {
    this.patchState('set selected', { selectedIds });
  }

  /**
   * Updates the list of results after filtering and sorting.
   */
  private getResults() {
    return GridUtilsV2.getResults(this._allResults$.value, {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
      filterFnMap: {
        name: GridUtilsV2.multiselectFilter,
        serial: GridUtilsV2.multiselectFilter,
        company: GridUtilsV2.multiselectFilter,
        _groups: (groups: string[], filterValue: string[]) => {
          if (filterValue.length === 0) {
            return true;
          }
          return groups.some((group) => filterValue.includes(group));
        },
        plant: GridUtilsV2.multiselectFilter,
      },
      resultComparator: GridUtilsV2.defaultResultComparator,
    });
  }
}
